<template>
  <div class="fullscreen-page" @click="closeMenu">
    <HeaderMenu title="Eating Time Tracker" />

    <main class="app-main">
      <section class="tracker-section">
        <div class="card">
          <div class="input-group">
            <label for="eating-time">New Eating Time:</label>
            <div class="datetime-input-group">
              <input
                id="eating-time"
                v-model="lastEatingTime"
                type="datetime-local"
              />
              <button @click="setToNow" class="secondary btn now-btn" title="Set to current time">
                Now
              </button>
            </div>
            <div class="adjustment-buttons">
              <button @click="adjustTime(-86400000, false)" class="secondary btn adj-btn" title="Subtract 1 day">-1d</button>
              <button @click="adjustTime(-3600000, true)" class="secondary btn adj-btn" title="Subtract 1 hour">-1h</button>
              <button @click="adjustTime(-900000, true)" class="secondary btn adj-btn" title="Subtract 15 minutes">-15m</button>
              <button @click="adjustTime(900000, true)" class="secondary btn adj-btn" title="Add 15 minutes">+15m</button>
              <button @click="adjustTime(3600000, true)" class="secondary btn adj-btn" title="Add 1 hour">+1h</button>
              <button @click="adjustTime(86400000, false)" class="secondary btn adj-btn" title="Add 1 day">+1d</button>
            </div>
            <button @click="recordEating" class="primary btn" :disabled="loading">
              {{ loading ? 'Recording...' : 'Record Eating' }}
            </button>
          </div>

          <div class="info-display">
            <div class="eating-record-header">
              <p class="label">Last Recorded Eating:</p>
              <button 
                v-if="latestEatingRecord" 
                @click="deleteLatestRecord" 
                class="delete-btn"
                title="Delete this record"
              >
                🗑️
              </button>
            </div>
            <p class="value">{{ latestEatingRecord ? formatDateTimeWithSettings(latestEatingRecord.eating_time) : 'No records yet' }}</p>
          </div>

          <div class="info-display">
            <p class="label">Next Eating Available:</p>
            <p class="value">{{ personalNextEating || 'No records yet' }}</p>
            <p class="time-until">{{ timeUntilNext || 'Record an eating time to see countdown' }}</p>
          </div>

          <div class="info-display">
            <p class="label">Group's Next Eating:</p>
            <p class="value">{{ groupNextEating || 'No records yet' }}</p>
            <p class="time-until">{{ groupTimeUntilNext || 'Select a group to see countdown' }}</p>
          </div>

          <div v-if="groupMembers.length > 0" class="members-list">
            <h3>Group Members</h3>
            <ul>
              <li v-for="member in groupMembers" :key="member.id">
                <span class="member-name">{{ member.username }}</span>
                <span v-if="member.eating_time" class="member-time">
                  {{ formatDateTimeWithSettings(member.eating_time) }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import HeaderMenu from '../components/HeaderMenu.vue'
import { useAuth } from '../composables/useAuth'
import { useSettings } from '../composables/useSettings'
import { useNotifications } from '../composables/useNotifications'
import { API } from '../api/client'

const { addNotification } = useNotifications()

interface EatingRecord {
  id: string
  user_id: string
  eating_time: string
  created_at: string
}

interface GroupMember {
  id: string
  username: string
  eating_time?: string
}

const { user } = useAuth()
const { settings, formatDateTimeRelative, formatInterval, loadSettings } = useSettings()

const lastEatingTime = ref('')
const groupMembers = ref<GroupMember[]>([])
const latestEatingRecord = ref<EatingRecord | null>(null)
const loading = ref(false)

const personalNextEating = computed(() => {
  if (!latestEatingRecord.value) return ''
  const eating = new Date(latestEatingRecord.value.eating_time)
  const next = new Date(eating.getTime() + settings.value.fasting_interval * 60 * 60 * 1000)
  return formatDateTimeRelative(next)
})

const timeUntilNext = computed(() => {
  if (!latestEatingRecord.value) return ''
  const now = new Date()
  const eating = new Date(latestEatingRecord.value.eating_time)
  
  const next = new Date(eating.getTime() + settings.value.fasting_interval * 60 * 60 * 1000)
  const diff = next.getTime() - now.getTime()

  if (diff <= 0) return 'Ready to eat now!'

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  return `${hours}h ${minutes}m remaining`
})

const groupNextEating = computed(() => {
  if (!groupMembers.value.length) return ''

  const latestEating = groupMembers.value
    .filter((m) => m.eating_time)
    .map((m) => new Date(m.eating_time || ''))
    .sort((a, b) => b.getTime() - a.getTime())[0]

  if (!latestEating) return 'No eating records'

  const next = new Date(latestEating.getTime() + settings.value.fasting_interval * 60 * 60 * 1000)
  return formatDateTimeRelative(next)
})

const groupTimeUntilNext = computed(() => {
  if (!groupMembers.value.length) return ''
  
  const latestEating = groupMembers.value
    .filter((m) => m.eating_time)
    .map((m) => new Date(m.eating_time || ''))
    .sort((a, b) => b.getTime() - a.getTime())[0]

  if (!latestEating) return ''

  const now = new Date()
  const next = new Date(latestEating.getTime() + settings.value.fasting_interval * 60 * 60 * 1000)
  const diff = next.getTime() - now.getTime()

  if (diff <= 0) return 'Group can eat now!'

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  return `${hours}h ${minutes}m remaining`
})

const recordEating = async () => {
  if (!lastEatingTime.value) {
    addNotification('Please select an eating time', { type: 'warning' })
    return
  }

  try {
    loading.value = true
    await API.recordEating(lastEatingTime.value)
    lastEatingTime.value = ''
    addNotification('Eating time recorded!', { type: 'success' })
    await loadLatestEatingRecord()
    await loadFavoriteGroupMembers()
  } catch (error) {
    addNotification('Failed to record eating time', { type: 'error' })
  } finally {
    loading.value = false
  }
}

const deleteLatestRecord = async () => {
  if (!latestEatingRecord.value) {
    addNotification('No eating records to delete', { type: 'warning' })
    return
  }

  if (!confirm('Are you sure you want to delete this eating record?')) {
    return
  }

  try {
    await API.deleteLatestEatingRecord()
    addNotification('Eating record deleted successfully!', { type: 'success' })
    await loadLatestEatingRecord()
    await loadFavoriteGroupMembers()
  } catch (error: any) {
    console.error('Failed to delete eating record:', error)
    addNotification(error.message || 'Failed to delete eating record', { type: 'error' })
  }
}

const setToNow = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  
  lastEatingTime.value = `${year}-${month}-${day}T${hours}:${minutes}`
}

const adjustTime = (deltaMs: number, roundToQuarterHour: boolean) => {
  const base = lastEatingTime.value ? new Date(lastEatingTime.value) : new Date()

  let minutes = base.getMinutes()
  if (roundToQuarterHour) {
    minutes = Math.round(minutes / 15) * 15
    base.setMinutes(minutes, 0, 0)
  }

  const adjusted = new Date(base.getTime() + deltaMs)
  const year = adjusted.getFullYear()
  const month = String(adjusted.getMonth() + 1).padStart(2, '0')
  const day = String(adjusted.getDate()).padStart(2, '0')
  const hours = String(adjusted.getHours()).padStart(2, '0')
  const mins = String(adjusted.getMinutes()).padStart(2, '0')

  lastEatingTime.value = `${year}-${month}-${day}T${hours}:${mins}`
}

const loadLatestEatingRecord = async () => {
  try {
    latestEatingRecord.value = await API.getLatestEatingRecord()
  } catch (error) {
    console.error('Failed to load latest eating record:', error)
  }
}

const loadFavoriteGroupMembers = async () => {
  try {
    groupMembers.value = await API.getFavoriteGroupLatestEatingRecords()
  } catch (error) {
    console.error('Failed to load favorite group members:', error)
  }
}

const formatDateTimeWithSettings = (dateString: string) => {
  return formatDateTimeRelative(dateString)
}

const handleNavigation = (page: 'tracker' | 'groups' | 'settings') => {
  // Navigation is handled by router, no need for emit
}

const closeMenu = () => {
  // Menu closes when clicking outside
}

onMounted(() => {
  loadSettings()
  loadLatestEatingRecord()
  loadFavoriteGroupMembers()
})
</script>

<style scoped>
.app-main {
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.tracker-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  .card {
	gap: 1.25em;
  }
}

.tracker-section h2 {
  margin: 0 0 0.5rem;
  font-size: 1.3rem;
  color: #333;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-group label {
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
}

.datetime-input-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.input-group input,
.input-group select {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
}

.input-group input:focus,
.input-group select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.now-btn {
  flex: 0 0 auto;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
}

.adjustment-buttons {
  display: flex;
  gap: 0.25rem;
}

.adj-btn {
  flex: 1;
  min-width: 0;
  padding: 0.4rem 0.5rem;
  font-size: 0.8rem;
}

.info-display {
	background: white;
	border-left: 4px solid var(--theme-color-4);
	padding: 1rem;
	border-radius: 4px;
	& .label {
		margin: 0;
		font-size: 0.9rem;
		color: #666;
	}
	& .value {
		margin: 0 0 0.75rem;
		font-size: 1.1rem;
		font-weight: 600;
		color: #333;
	}

	& .time-until {
		margin: 0;
		font-size: 0.9rem;
		color: var(--theme-color-3);
		font-weight: 500;
	}
}

.eating-record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

#eating-time {
	flex-grow: 1;
}

.delete-btn {
	background: none;
	border: none;
	cursor: pointer;
	font-size: 1.2rem;
	padding: 0.25rem;
	border-radius: 4px;
	transition: background-color 0.2s;
	&:hover:not(:disabled) {
		background-color: #ffe6e6;
	}

	&:active {
		transform: scale(0.95);
	}
}

.members-list {
  margin-top: 0.5rem;
}

.members-list h3 {
  margin: 0 0 0.75rem;
  font-size: 1rem;
  color: #333;
}

.members-list ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.members-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 4px;
  font-size: 0.9rem;
}

.member-name {
  font-weight: 500;
  color: #333;
}

.member-time {
  color: #666;
  font-size: 0.85rem;
}
</style>